/**
 * Focus helpers for screens whose targets re-mount after a query refetch
 * (V102-8-3 PR B round 6: focus must be re-applied after the refetch, not once).
 */
import { useLayoutEffect, useRef } from 'react';

/** Focus an element once it is mounted again (after the re-render that brings it back). */
export function useFocusWhenMounted() {
  const pending = useRef<(() => HTMLElement | null) | null>(null);
  useLayoutEffect(() => {
    if (!pending.current) return;
    const el = pending.current();
    if (el) { el.focus(); pending.current = null; }
  });
  return (get: () => HTMLElement | null) => { pending.current = get; };
}

/**
 * Focus a target after the server data settles. A refetch can re-mount the target as a new
 * element (focus drops to BODY), so keep re-applying it — only while focus sits on BODY or on the
 * target — until it has held for a few frames.
 */
// The latest focus intent wins: starting a new settle loop ends any earlier one, so a slow loop from a
// previous action (e.g. "focus the record heading after save") can't pull focus back after the user
// has acted again (e.g. Cancel → "return focus to Change reviewer").
let settleGeneration = 0;

export function focusAfterSettle(get: () => HTMLElement | null, maxFrames = 60) {
  const generation = ++settleGeneration;
  let frames = 0;
  let held = 0;
  // The element focused when the action started (e.g. a form's submit button that is about to unmount).
  const from = document.activeElement;
  const tick = () => {
    if (generation !== settleGeneration) return; // superseded by a newer focus intent
    const el = get();
    const active = document.activeElement;
    // Focus still on the element the action started from (not yet unmounted on a slow frame), or on a
    // detached node, is not the user moving on. Anything newly focused is, and is never taken back.
    const outgoing = !!active && active !== document.body && (!active.isConnected || active === from);
    if (el && (active === el || active === document.body || active === null || outgoing)) {
      if (active !== el) { el.focus(); held = 0; } else held++;
    } else if (el) return; // the user moved on
    if (held < 3 && frames++ < maxFrames) requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
}
